import { useEffect, useState } from "react";

import { IgAlert } from "@intelligems/ig-theme/IgAlert";
import { IgChip } from "@intelligems/ig-theme/IgChip";
import { IgDialog } from "@intelligems/ig-theme/IgDialog";
import { IgDivider } from "@intelligems/ig-theme/IgDivider";
import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";

import type { WorkspacePublishReviewConfig } from "../../../domainConfig";
import type { PublishBlockReason, WorkspaceGateResult } from "../../../types";
import { ReviewRow } from "../ReviewRow";

/** Publish review modal (opened by the Publish button). */
export interface PublishReviewModalProps<GateKey extends string> {
  open: boolean;
  onClose: () => void;
  onConfirm: (notes?: string, title?: string) => void;
  /**
   * Prefill for the version-title field: `"Built from chat"` when the draft's
   * source came from the AI chat, else empty.
   */
  defaultTitle: string;
  /** Version number that will be published (active version + 1, or 1). */
  versionToPublish: number;
  /** Label for the version currently live, or "none". */
  previousVersionLabel: string;
  /** Domain copy: block reasons, extra rows, gate summary, impact sentence. */
  config: WorkspacePublishReviewConfig<GateKey>;
  gates: WorkspaceGateResult<GateKey>[];
  /** When false, show the blocking warning and disable confirm. */
  canPublish: boolean;
  /**
   * The single reason publish is blocked, or `null` when permitted. Drives the
   * blocked-alert title/message and the disabled-button tooltip so the copy
   * reflects the true blocker. `null` exactly when `canPublish` is `true`.
   */
  publishBlockReason: PublishBlockReason | null;
  isPublishing: boolean;
}

const findGate = <GateKey extends string>(
  gates: WorkspaceGateResult<GateKey>[],
  key: GateKey
): WorkspaceGateResult<GateKey> | undefined =>
  gates.find((g) => g.gateKey === key);

/**
 * A pass/fail status chip derived from a gate's status. `passed` → success,
 * `failed`/`blocked` → error, anything else (pending/running/warning) → neutral
 * "Pending" so the row never falsely claims a gate has passed.
 */
const GateStatusChip = <GateKey extends string>({
  gate,
}: {
  gate: WorkspaceGateResult<GateKey> | undefined;
}) => {
  if (gate?.status === "passed") {
    return <IgChip size="small" color="success" label="Passed" />;
  }
  if (gate?.status === "failed" || gate?.status === "blocked") {
    return <IgChip size="small" color="error" label="Failed" />;
  }
  return <IgChip size="small" label="Pending" />;
};

/**
 * Publish review modal. Summarizes exactly what a publish will ship — the
 * version, the domain's own summary rows, and the gate statuses that matter
 * most. When publish is blocked, a warning block is shown and the primary
 * button is disabled; `publishBlockReason` names the actual blocker (failed
 * gate, unsaved changes, invalid syntax, …) so the copy reflects the true cause
 * rather than always blaming the gates.
 */
export const PublishReviewModal = <GateKey extends string>({
  open,
  onClose,
  onConfirm,
  defaultTitle,
  versionToPublish,
  previousVersionLabel,
  config,
  gates,
  canPublish,
  publishBlockReason,
  isPublishing,
}: PublishReviewModalProps<GateKey>) => {
  const theme = useTheme();
  const [notes, setNotes] = useState("");
  const [title, setTitle] = useState(defaultTitle);

  const blockCopy = publishBlockReason
    ? config.blockCopy[publishBlockReason]
    : null;

  useEffect(() => {
    if (!open) {
      setNotes("");
    }
    // Re-seed the title prefill on every open so it tracks the draft's current
    // provenance rather than whatever a previous open captured.
    setTitle(defaultTitle);
  }, [open, defaultTitle]);

  const content = (
    <Stack spacing={2}>
      {blockCopy && !isPublishing && (
        <IgAlert
          alertKey="publish-review-blocked"
          severity="warning"
          title={blockCopy.title}
          message={blockCopy.message}
        />
      )}

      <Stack spacing={1.5}>
        <ReviewRow label="Version to publish">
          <IgTypography
            variant="body2"
            sx={{ fontFamily: "monospace", fontWeight: 600 }}
          >
            v{versionToPublish}
          </IgTypography>
          <IgTypography variant="caption" color="text.secondary">
            (from {previousVersionLabel})
          </IgTypography>
        </ReviewRow>

        {config.extraRows}

        <IgTextField
          label="Version title (optional)"
          placeholder="e.g., Built from chat"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          slotProps={{ htmlInput: { maxLength: 255 } }}
        />

        <IgTextField
          label="Version notes (optional)"
          placeholder="e.g., First rollout for holiday rate updates"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          multiline
          minRows={2}
        />

        <IgDivider />

        {config.gateSummaryRows.map((row) => (
          <ReviewRow key={row.gateKey} label={row.label}>
            <GateStatusChip gate={findGate(gates, row.gateKey)} />
          </ReviewRow>
        ))}
      </Stack>

      <IgTypography variant="caption" color={theme.palette.text.secondary}>
        {config.impactCopy(versionToPublish)}
      </IgTypography>
    </Stack>
  );

  return (
    <IgDialog
      showDialog={open}
      title="Publish review"
      handleClose={onClose}
      handleNoClick={onClose}
      handleYesClick={() =>
        onConfirm(notes.trim() || undefined, title.trim() || undefined)
      }
      maxWidth={"lg"}
      yesButtonText="Publish"
      yesButtonDisabled={!canPublish}
      yesButtonDisabledTooltip={
        blockCopy?.tooltip ?? "Resolve all required gates before publishing"
      }
      loading={isPublishing}
      shouldNotClose
      content={content}
    />
  );
};
